/**
 * TOML parser — handles the subset we use:
 * strings, numbers, booleans, arrays, inline tables, [[array-of-tables]], multi-line values.
 * Zero dependencies.
 */

type TomlPrimitive = string | number | boolean;
interface TomlObject { [key: string]: TomlValue }
type TomlValue = TomlPrimitive | TomlObject | TomlValue[];
type TomlDoc = Record<string, TomlValue>;

function trimQuotes(s: string): string {
  return (s.startsWith('"') && s.endsWith('"')) ? s.slice(1, -1) : s;
}

function parseTomlValue(raw: string): TomlValue {
  const val = raw.trim();

  // Double-quoted string
  if (val.startsWith('"') && val.endsWith('"')) return val.slice(1, -1);

  // Multi-line inline table or array — parse char by char
  if (val.startsWith('{') || val.startsWith('[')) {
    return parseComplex(val);
  }

  // Float
  if (/^-?\d+\.\d+$/.test(val)) return parseFloat(val);
  // Integer
  if (/^-?\d+$/.test(val)) return parseInt(val, 10);
  // Boolean
  if (val === 'true') return true;
  if (val === 'false') return false;
  // Fallback
  return val;
}

/** Parse an inline table or array with char-by-char, nesting-aware parsing */
function parseComplex(raw: string): TomlValue {
  const s = raw.trim();
  if (s.startsWith('{')) return parseInlineTableBody(s);
  if (s.startsWith('[')) return parseArrayBody(s);
  return s;
}

/** Parse { key = val, key2 = val2 } including nested braces/brackets */
function parseInlineTableBody(raw: string): TomlObject {
  const table: TomlObject = {};
  const s = raw.trim();
  if (s === '{}') return table;
  // Strip outer braces
  const inner = s.slice(1, -1).trim();
  if (!inner) return table;

  // Split on top-level commas (not inside nested {} or [] or quotes)
  const parts = splitTopLevel(inner, ',');
  for (const part of parts) {
    const eqIdx = findTopLevel(part, '=');
    if (eqIdx === -1) continue;
    const key = trimQuotes(part.slice(0, eqIdx).trim());
    const val = part.slice(eqIdx + 1).trim();
    table[key] = parseTomlValue(val);
  }
  return table;
}

/** Parse array [a, b, c] including nested arrays/tables */
function parseArrayBody(raw: string): TomlValue[] {
  const s = raw.trim();
  if (s === '[]') return [];
  const inner = s.slice(1, -1).trim();
  if (!inner) return [];
  const parts = splitTopLevel(inner, ',');
  return parts.map(p => parseTomlValue(p.trim()));
}

/** Split on separator that's not inside quotes, nested {}, or nested [] */
function splitTopLevel(text: string, separator: string): string[] {
  const result: string[] = [];
  let depth = 0;
  let inString = false;
  let start = 0;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"' && (i === 0 || text[i - 1] !== '\\')) {
      inString = !inString;
    }
    if (!inString) {
      if (ch === '{' || ch === '[') depth++;
      if (ch === '}' || ch === ']') depth--;
    }
    if (!inString && depth === 0 && ch === separator) {
      result.push(text.slice(start, i));
      start = i + 1;
    }
  }
  result.push(text.slice(start));
  return result.map(s => s.trim()).filter(s => s.length > 0);
}

/** Find index of char at top level (not in quotes or nested) */
function findTopLevel(text: string, char: string): number {
  let depth = 0;
  let inString = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"' && (i === 0 || text[i - 1] !== '\\')) inString = !inString;
    if (!inString) {
      if (ch === '{' || ch === '[') depth++;
      if (ch === '}' || ch === ']') depth--;
    }
    if (!inString && depth === 0 && ch === char) return i;
  }
  return -1;
}

/** Merge continuation lines (multi-line arrays/tables) into single logical lines */
function mergeMultiLine(lines: string[]): string[] {
  const merged: string[] = [];
  let buffer = '';

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) {
      // If we're buffering, this is a blank/comment inside a multi-line value — skip
      continue;
    }

    if (buffer) {
      // We're inside a multi-line value — append this line
      buffer += ' ' + trimmed;
      // Check if the multi-line value is now closed
      if (isBalanced(buffer)) {
        merged.push(buffer);
        buffer = '';
      }
    } else {
      // Check if this line starts a multi-line value
      if (isMultiLineStart(trimmed)) {
        buffer = trimmed;
        if (isBalanced(buffer)) {
          merged.push(buffer);
          buffer = '';
        }
      } else {
        merged.push(trimmed);
      }
    }
  }

  if (buffer) merged.push(buffer); // Shouldn't happen with valid TOML

  return merged;
}

function isMultiLineStart(line: string): boolean {
  // Key = value where value starts with [ or {
  const eqIdx = findTopLevel(line, '=');
  if (eqIdx === -1) return false;
  const val = line.slice(eqIdx + 1).trim();
  return val.startsWith('[') || val.startsWith('{');
}

function isBalanced(line: string): boolean {
  let depth = 0;
  let inString = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"' && (i === 0 || line[i - 1] !== '\\')) inString = !inString;
    if (inString) continue;
    if (ch === '{' || ch === '[') depth++;
    if (ch === '}' || ch === ']') depth--;
  }
  return depth === 0 && !inString;
}

export function parseToml(text: string): TomlDoc {
  const result: TomlDoc = {};
  const rawLines = text.split('\n');
  const lines = mergeMultiLine(rawLines);

  let currentTable: Record<string, TomlValue> = result;
  let currentArray: TomlValue[] | null = null;
  let currentArrayTable: Record<string, TomlValue> | null = null;

  for (const line of lines) {
    // [[ name ]] — array of tables
    const arrayMatch = line.match(/^\[\[(.+)\]\]$/);
    if (arrayMatch) {
      const key = arrayMatch[1]!.trim();
      if (!result[key]) result[key] = [];
      currentArray = result[key] as TomlValue[];
      currentArrayTable = {};
      currentArray.push(currentArrayTable);
      continue;
    }

    // [ name ] — table
    const tableMatch = line.match(/^\[(.+)\]$/);
    if (tableMatch) {
      const key = tableMatch[1]!.trim();
      if (!result[key]) result[key] = {};
      currentTable = result[key] as Record<string, TomlValue>;
      currentArray = null;
      currentArrayTable = null;
      continue;
    }

    // key = value
    const eqIdx = findTopLevel(line, '=');
    if (eqIdx === -1) continue;

    const key = line.slice(0, eqIdx).trim();
    const rawValue = line.slice(eqIdx + 1).trim();
    const value = parseTomlValue(rawValue);
    const target = currentArrayTable ?? currentTable;
    target[key] = value;
  }

  return result;
}
